import React, { useState } from 'react';
import {
  Table, Form, Button, InputGroup, Row, Col,
} from 'react-bootstrap';
import {
  VAT_OPTIONS, lineNet, lineGross, formatAmount, vatHint,
} from '../../Utils/billingoDocuments';

/**
 * What the trip's route implies for this line's VAT — a hint when the
 * operator's choice matches it, a warning when it differs, and NOTHING when
 * the route could not be established.
 *
 * IT MUST NEVER BLOCK. No disabled control, no submit guard, no
 * `FinalInvoiceBlockedAlert` (that mechanism is for the advance-refusal case,
 * which does block, and must not be reused here). The rule disagrees with
 * 224 of 1,455 live document lines in production (2026-11-02) and ~140 of
 * those disagreements are unresolved questions for the accountant, so an
 * operator who overrides it is informed, not stopped.
 *
 * `basis` is shown because it is what makes the warning actionable: "drop-off:
 * Vienna International Airport" tells the operator which leg decided it, so
 * they can agree or disagree on the facts rather than on an opinion.
 */
function VatRouteHint({ item }) {
  const hint = vatHint(item);
  if (!hint) return null;
  const differs = hint.tone === 'differs';
  return (
    <div className={`small mt-1 ${differs ? 'text-warning-emphasis fw-semibold' : 'text-muted'}`}>
      {differs && <i className="fa fa-triangle-exclamation me-1" />}
      {differs ? `Route suggests ${hint.rate}%` : `${hint.rate}% matches the route`}
      <div className="fw-normal text-muted">{hint.basis}</div>
    </div>
  );
}

// Step 3b — editable table of the selected lines. Each row keeps its
// originating order_reference. Számla deduction rows (negative advances) are
// rendered read-only at the bottom. A bulk toolbar overwrites the description
// or VAT on every line at once, and a footer shows running net/VAT/gross totals.
function BillingoLineItemsEditor({
  items, deductionItems = [], totals = null, onChange, onBulkChange, onRemove,
}) {
  const [bulkDescription, setBulkDescription] = useState('');

  const STRING_FIELDS = ['description', 'comment'];
  const patch = (key, field) => (e) => {
    const raw = e.target.value;
    const value = STRING_FIELDS.includes(field) ? raw : Number(raw);
    onChange(key, { [field]: value });
  };

  const applyBulkDescription = () => {
    if (onBulkChange && bulkDescription.trim()) {
      onBulkChange({ description: bulkDescription.trim() });
    }
  };
  // Set one VAT rate on every line. DELIBERATELY UNCHANGED by the route hint,
  // and deliberately NOT given a bulk-level warning of its own: a bulk set that
  // contradicts the derivation already lights up the per-row warning on exactly
  // the rows it contradicts, which is strictly more useful than one toolbar
  // message that cannot name them. There is also no "set all to the suggested
  // rate" button, because on a multi-order document the suggestion is per
  // booking and a single rate would be the wrong answer for some rows.
  const applyBulkVat = (e) => {
    if (onBulkChange) onBulkChange({ vat: Number(e.target.value) });
  };

  const byCurrency = totals?.byCurrency || {};
  const totalCurrencies = Object.keys(byCurrency);

  return (
    <>
      {onBulkChange && items.length > 0 && (
        <Row className="g-2 align-items-end mb-2">
          <Col xs={12} md={7}>
            <Form.Label className="small text-muted mb-1">Set all descriptions</Form.Label>
            <InputGroup size="sm">
              <Form.Control
                placeholder="Overwrite every line's description…"
                value={bulkDescription}
                onChange={(e) => setBulkDescription(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') applyBulkDescription(); }}
              />
              <Button variant="outline-secondary" onClick={applyBulkDescription} disabled={!bulkDescription.trim()}>
                Apply to all
              </Button>
            </InputGroup>
          </Col>
          <Col xs={12} md={5}>
            <Form.Label className="small text-muted mb-1">Set all VAT %</Form.Label>
            <Form.Select size="sm" value="" onChange={applyBulkVat}>
              <option value="" disabled>Overwrite every line&apos;s VAT…</option>
              {VAT_OPTIONS.map((v) => <option key={v} value={v}>{`${v}%`}</option>)}
            </Form.Select>
          </Col>
        </Row>
      )}

      <Table responsive bordered size="sm" className="align-middle">
        <thead className="table-light">
          <tr>
            <th>Order</th>
            <th style={{ minWidth: 200 }}>Description</th>
            <th style={{ minWidth: 180 }}>Comment</th>
            <th className="text-end" style={{ width: 80 }}>Qty</th>
            <th className="text-end" style={{ width: 120 }}>Unit price</th>
            <th style={{ width: 150 }}>VAT %</th>
            <th style={{ width: 80 }}>Currency</th>
            <th className="text-end" style={{ width: 120 }}>Net</th>
            <th className="text-end" style={{ width: 120 }}>Gross</th>
            <th style={{ width: 40 }} aria-label="actions" />
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && (
            <tr>
              <td colSpan={10} className="text-center text-muted py-3">
                No lines selected yet.
              </td>
            </tr>
          )}
          {items.map((item) => (
            <tr key={item.key}>
              <td className="small text-muted">{item.order_reference}</td>
              <td>
                <Form.Control size="sm" value={item.description} onChange={patch(item.key, 'description')} />
              </td>
              <td>
                <Form.Control size="sm" value={item.comment || ''} onChange={patch(item.key, 'comment')} placeholder="Order line name" />
              </td>
              <td>
                <Form.Control size="sm" type="number" className="text-end" value={item.quantity} onChange={patch(item.key, 'quantity')} />
              </td>
              <td>
                <Form.Control size="sm" type="number" className="text-end" value={item.unit_price} onChange={patch(item.key, 'unit_price')} />
              </td>
              <td style={{ minWidth: 150 }}>
                <Form.Select size="sm" value={item.vat} onChange={patch(item.key, 'vat')}>
                  {VAT_OPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
                </Form.Select>
                <VatRouteHint item={item} />
              </td>
              <td className="small">{item.currency}</td>
              <td className="text-end">{formatAmount(lineNet(item), item.currency)}</td>
              <td className="text-end">{formatAmount(lineGross(item), item.currency)}</td>
              <td className="text-center">
                <Button variant="link" className="text-danger p-0" onClick={() => onRemove(item.key)} aria-label="Remove line">
                  <i className="fa fa-times" />
                </Button>
              </td>
            </tr>
          ))}
          {deductionItems.map((item) => (
            <tr key={item.key} className="table-warning">
              <td className="small text-muted">—</td>
              <td>{item.description}</td>
              <td className="small text-muted">{item.comment || '—'}</td>
              <td className="text-end">{item.quantity}</td>
              <td className="text-end">{formatAmount(item.unit_price, item.currency)}</td>
              <td>{item.vat}</td>
              <td className="small">{item.currency}</td>
              <td className="text-end">{formatAmount(lineNet(item), item.currency)}</td>
              <td className="text-end">{formatAmount(lineGross(item), item.currency)}</td>
              <td />
            </tr>
          ))}
        </tbody>
        {totalCurrencies.length > 0 && (
          <tfoot className="table-light fw-semibold">
            {totalCurrencies.map((cur) => (
              <tr key={cur}>
                <td colSpan={7} className="text-end">{`Total (${cur})`}</td>
                <td className="text-end">{formatAmount(byCurrency[cur].net, cur)}</td>
                <td className="text-end">{formatAmount(byCurrency[cur].gross, cur)}</td>
                <td />
              </tr>
            ))}
          </tfoot>
        )}
      </Table>
    </>
  );
}

export default BillingoLineItemsEditor;
